import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaLock, FaShieldAlt, FaCamera, FaSave } from 'react-icons/fa';
import {
  getAccount, updateAccount, changePassword, enable2FA, disable2FA,
  uploadProfilePhoto, uploadProfileEmbedding, getImageUrl,
} from '../services/api';
import { compressImageForUpload } from '../utils/imageCompression';

const normalizeAccountTab = (tab) => {
  if (tab === '2fa') return 'security';
  return ['profile', 'password', 'security'].includes(tab) ? tab : 'profile';
};

const AccountSettings = () => {
  const { user, setUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab = normalizeAccountTab(requestedTab);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: ''
  });
  
  // Password data
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // 2FA data
  const [twoFAData, setTwoFAData] = useState({
    enabled: false,
    secret: '',
    qr_uri: ''
  });
  
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    fetchAccountData();
  }, []);

  useEffect(() => {
    setActiveTab(normalizeAccountTab(requestedTab));
  }, [requestedTab]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'profile' ? {} : { tab });
  };

  const fetchAccountData = async () => {
    try {
      const response = await getAccount();
      setProfileData({
        name: response.name,
        email: response.email,
        phone: response.phone || '',
        bio: response.bio || ''
      });
      setTwoFAData({
        enabled: response.two_factor_enabled,
        secret: '',
        qr_uri: ''
      });
    } catch (error) {
      console.error('Error fetching account data:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await updateAccount(profileData);
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès !' });

      const updatedUser = { ...user, ...(response.user || {}), name: profileData.name };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'La mise à jour a échoué' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }

    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères' });
      return;
    }

    setLoading(true);

    try {
      await changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setMessage({ type: 'success', text: 'Mot de passe modifié avec succès !' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'La modification du mot de passe a échoué' });
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await enable2FA();
      setTwoFAData({
        enabled: true,
        secret: response.secret,
        qr_uri: response.qr_uri
      });
      setMessage({ type: 'success', text: "Authentification à deux facteurs activée ! Scannez le QR code avec votre application d'authentification." });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || "Impossible d'activer l'authentification à deux facteurs" });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      setMessage({ type: 'error', text: 'Veuillez saisir votre mot de passe' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await disable2FA({ password: disablePassword });
      setTwoFAData({ enabled: false, secret: '', qr_uri: '' });
      setDisablePassword('');
      setMessage({ type: 'success', text: 'Authentification à deux facteurs désactivée avec succès !' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || "Impossible de désactiver l'authentification à deux facteurs" });
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Veuillez importer une image JPG, PNG ou WEBP.' });
      return;
    }

    if (file.size > 16 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'La photo de profil doit faire 16 Mo ou moins avant compression.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const uploadFile = await compressImageForUpload(file);
      const formData = new FormData();
      formData.append('photo', uploadFile);

      const response = await uploadProfilePhoto(formData);
      const embeddingFormData = new FormData();
      embeddingFormData.append('photo', uploadFile);
      const embeddingResponse = await uploadProfileEmbedding(embeddingFormData);

      setMessage({
        type: 'success',
        text: `Photo de profil mise à jour et encodée avec ${embeddingResponse.model || 'DeepFace'}.`,
      });

      const updatedUser = { ...user, profile_photo: response.profile_photo, face_encoded: true };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || "L'import a échoué" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '900px', padding: '2rem' }}>
      <h1 style={{ color: 'var(--primary-orange)', marginBottom: '2rem' }}>
        Paramètres du compte
      </h1>

      {message.text && (
        <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid var(--light-gray)' }}>
        <button
          onClick={() => selectTab('profile')}
          style={{
            padding: '1rem 2rem',
            background: activeTab === 'profile' ? 'var(--primary-orange)' : 'transparent',
            color: activeTab === 'profile' ? 'white' : 'var(--dark-gray)',
            border: 'none',
            borderBottom: activeTab === 'profile' ? '3px solid var(--primary-orange)' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          <FaUser /> Profil
        </button>
        <button
          onClick={() => selectTab('password')}
          style={{
            padding: '1rem 2rem',
            background: activeTab === 'password' ? 'var(--primary-orange)' : 'transparent',
            color: activeTab === 'password' ? 'white' : 'var(--dark-gray)',
            border: 'none',
            borderBottom: activeTab === 'password' ? '3px solid var(--primary-orange)' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          <FaLock /> Mot de passe
        </button>
        <button
          onClick={() => selectTab('security')}
          style={{
            padding: '1rem 2rem',
            background: activeTab === 'security' ? 'var(--primary-orange)' : 'transparent',
            color: activeTab === 'security' ? 'white' : 'var(--dark-gray)',
            border: 'none',
            borderBottom: activeTab === 'security' ? '3px solid var(--primary-orange)' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          <FaShieldAlt /> Double authentification
        </button>
      </div>

      {activeTab === 'profile' && (
        <div className="auth-container" style={{ maxWidth: '100%' }}>
          <h2 style={{ color: 'var(--primary-green)' }}>Informations du profil</h2>
          
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {user?.profile_photo ? (
                <img
                  src={getImageUrl(user.profile_photo)}
                  alt="Profil"
                  style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'var(--light-gray)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem',
                  color: 'var(--gray)'
                }}>
                  <FaUser />
                </div>
              )}
              <label
                htmlFor="profilePhotoInput"
                style={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  background: 'var(--primary-orange)',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                <FaCamera />
              </label>
              <input
                id="profilePhotoInput"
                type="file"
                accept="image/*"
                onChange={handleProfilePhotoUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <form onSubmit={handleProfileUpdate}>
            <div className="form-group">
              <label>Nom</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>E-mail</label>
              <input
                type="email"
                value={profileData.email}
                disabled
                style={{ background: 'var(--off-white)', cursor: 'not-allowed' }}
              />
              <small style={{ color: 'var(--gray)' }}>L'e-mail ne peut pas être modifié</small>
            </div>

            <div className="form-group">
              <label>Téléphone (facultatif)</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                placeholder="+1 234 567 8900"
              />
            </div>

            <div className="form-group">
              <label>Bio (facultatif)</label>
              <textarea
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                rows="4"
                placeholder="Parlez-nous de vous..."
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              <FaSave /> {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'password' && (
        <div className="auth-container" style={{ maxWidth: '100%' }}>
          <h2 style={{ color: 'var(--primary-green)' }}>Changer le mot de passe</h2>
          
          <form onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label>Mot de passe actuel</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Nouveau mot de passe</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength="6"
              />
            </div>

            <div className="form-group">
              <label>Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              <FaLock /> {loading ? 'Modification...' : 'Changer le mot de passe'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="auth-container" style={{ maxWidth: '100%' }}>
          <h2 style={{ color: 'var(--primary-green)' }}>Authentification à deux facteurs</h2>
          
          {!twoFAData.enabled ? (
            <div>
              <p style={{ marginBottom: '1.5rem' }}>
                Ajoutez une couche de sécurité supplémentaire à votre compte en activant l'authentification à deux facteurs.
              </p>
              <button onClick={handleEnable2FA} className="btn-submit" disabled={loading}>
                <FaShieldAlt /> Activer la double authentification
              </button>
            </div>
          ) : (
            <div>
              <div style={{ padding: '1rem', background: 'var(--light-green)', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <strong style={{ color: 'var(--dark-green)' }}>Double authentification activée</strong>
              </div>

              {twoFAData.qr_uri && (
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <h3>Scannez ce QR code avec votre application d'authentification :</h3>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFAData.qr_uri)}`}
                    alt="2FA QR Code"
                    style={{ margin: '1rem auto' }}
                  />
                  <p style={{ fontSize: '0.9rem', color: 'var(--gray)' }}>
                    Secret : <code>{twoFAData.secret}</code>
                  </p>
                </div>
              )}

              <div className="form-group">
                <label>Saisissez votre mot de passe pour désactiver la double authentification</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Votre mot de passe"
                />
              </div>

              <button
                onClick={handleDisable2FA}
                className="btn-submit"
                disabled={loading}
                style={{ background: '#f44336' }}
              >
                Désactiver la double authentification
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountSettings;
